import { D } from '@mobily/ts-belt';
import {
  Chip,
  FormControl,
  FormHelperText,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import classnames from 'classnames';
import Downshift from 'downshift';
import React, { ComponentProps, useState } from 'react';
import { FieldTitle, InputHelperText, InputProps, useInput, useTranslate } from 'react-admin';

export type TextArrayInputProps = TextFieldProps &
  InputProps & {
    label?: string | false;
    variant?: 'filled' | 'standard' | 'outlined';
    helperText?: ComponentProps<typeof InputHelperText>['helperText'];
    maxItems?: number;
    asNumber?: boolean;
    itemCanBeDeleted?: (item: string) => boolean;
    testId?: string;
  };

export function TextArrayInput(props: TextArrayInputProps) {
  const classes = useStyles(props);
  const translate = useTranslate();
  const {
    disabled,
    helperText,
    label,
    margin = 'dense',
    variant = 'filled',
    resource,
    source,
    validate,
    maxItems,
    asNumber,
    testId,
    itemCanBeDeleted = (_item) => true,
  } = props;

  const {
    id,
    field: { value, onChange, onBlur },
    isRequired,
    fieldState: { error, isTouched },
    formState: { isSubmitted },
  } = useInput({
    resource,
    source,
    validate,
  });

  const values = Array.isArray(value) ? value : [];

  const [inputValue, setInputValue] = useState<string>('');

  function handleKeyDown(event: KeyboardEvent) {
    if ((event.key === 'Enter' || event.key === ',') && inputValue.length > 0) {
      onChange([...values, asNumber ? parseInt(inputValue, 10) : inputValue]);
      onBlur();
      setInputValue('');
      // do not handle text via handleInputChange
      event.preventDefault();
    }

    if (event.key === 'Backspace' && values.length > 0 && !inputValue.length) {
      onChange(values.slice(0, values.length - 1));
      onBlur();
    }
  }

  const handleDelete = (index: number) => () => {
    const newValues = [...values];
    newValues.splice(index, 1);
    onChange(newValues);
    onBlur();
  };

  function handleInputChange(eventOrValue: React.ChangeEvent<{ value: string }> | string) {
    const value = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue.target.value;

    if (asNumber) {
      setInputValue(value.replace(/\D/g, ''));
    } else {
      setInputValue(value);
    }
  }

  const handlePaste: React.ClipboardEventHandler = (event) => {
    const data = event.clipboardData.getData('text/plain');

    const parsedData = parseClipboardData(data, asNumber ?? false);

    if (parsedData.length) {
      // do not handle text via handleInputChange
      event.preventDefault();

      onChange([...values, ...parsedData]);
      onBlur();
    }
  };

  return (
    <FormControl
      error={!!((isTouched || isSubmitted) && error)}
      sx={{
        width: 'min-content',
        '& .MuiFormControl-root': {
          minWidth: 256,
          width: 'min-content !important',
          '& .MuiTextField-root': { minWidth: 256 },
        },
      }}>
      <Downshift inputValue={inputValue} selectedItem={values}>
        {({ getInputProps, getLabelProps }) => {
          const { onBlur, onChange, onFocus, color, size, ...rest } = getInputProps({
            onKeyDown: handleKeyDown,
            placeholder: typeof label === 'string' ? translate(label, { _: label }) : undefined,
          });
          const inputProps = D.deleteKeys(rest, ['ref', 'id']);
          return (
            <div className={classes.container}>
              <TextField
                id={id}
                error={(isTouched || isSubmitted) && !!error}
                InputProps={{
                  classes: {
                    root: classnames(classes.inputRoot, {
                      [classes.inputRootFilled]: variant === 'filled',
                    }),
                    input: classes.inputInput,
                  },
                  startAdornment:
                    values.length > 0 ? (
                      <div
                        className={classnames(classes.chipContainer, {
                          [classes.chipContainerFilled]: variant === 'filled',
                          [classes.chipContainerOutlined]: variant === 'outlined',
                        })}>
                        {values
                          .filter((item) => !!item)
                          .map((item, index) => (
                            <Chip
                              key={index}
                              tabIndex={-1}
                              label={item}
                              className={classes.chip}
                              onDelete={itemCanBeDeleted(item) ? handleDelete(index) : undefined}
                              title={item}
                            />
                          ))}
                      </div>
                    ) : null,
                  onBlur,
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                    handleInputChange(event);
                    if (onChange) {
                      onChange(event);
                    }
                  },
                  onPaste: handlePaste,
                  onFocus,
                }}
                label={
                  label !== '' &&
                  label !== false && (
                    <span>
                      <FieldTitle
                        label={label}
                        source={source}
                        resource={resource}
                        isRequired={isRequired}
                      />
                      <span>&thinsp;(press enter)</span>
                    </span>
                  )
                }
                InputLabelProps={getLabelProps({
                  htmlFor: id,
                })}
                variant={variant}
                margin={margin}
                // Use any because of wrong typing from Downshift
                /* eslint-disable @typescript-eslint/no-explicit-any */
                color={color as any}
                size={size as any}
                /* eslint-enable @typescript-eslint/no-explicit-any */
                disabled={disabled}
                {...inputProps}
                data-testid={testId}
              />
            </div>
          );
        }}
      </Downshift>
      {maxItems !== undefined ? (
        <Typography variant="caption" color={values.length > maxItems ? 'error' : undefined}>
          {values.length} / {maxItems}
        </Typography>
      ) : null}
      <FormHelperText error={(isTouched || isSubmitted) && !!error}>
        <InputHelperText
          touched={isTouched || isSubmitted}
          error={error?.message}
          helperText={helperText}
        />
      </FormHelperText>
    </FormControl>
  );
}

const CHIP_CONTAINER_MAX_HEIGHT = 160;

const useStyles = makeStyles(
  (theme) => ({
    container: {
      flexGrow: 1,
      position: 'relative',
    },
    chip: {
      margin: theme.spacing(0.5, 0.5, 0.5, 0),
    },
    chipContainer: {
      maxHeight: CHIP_CONTAINER_MAX_HEIGHT,
      overflowY: 'auto',
    },
    chipContainerFilled: {
      margin: theme.spacing(3, 0, 0.5, 0),
    },
    chipContainerOutlined: {
      margin: '12px 12px 10px 0',
    },
    inputRoot: {
      flexWrap: 'wrap',
    },
    inputRootFilled: {
      flexWrap: 'wrap',
      '& .inputRootFilled-chip': {
        backgroundColor:
          theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.09)' : 'rgba(255, 255, 255, 0.09)',
      },
    },
    inputInput: {
      width: 'auto',
      flexGrow: 1,
    },
  }),
  { name: 'TextArrayInput' },
);

export function parseClipboardData(data: string, asNumber: boolean): string[] | number[] {
  if (!(data.includes(',') || data.includes('\n'))) {
    return [];
  }

  const newValues = data
    .split(/[,\n]/)
    .map((val) => {
      const trimmed = val.trim();
      if (trimmed === '') {
        return null;
      }
      if (asNumber) {
        const parsed = parseInt(trimmed, 10);

        return isNaN(parsed) ? null : parsed;
      }
      return trimmed;
    })
    .filter((val) => val !== null);

  return newValues as string[] | number[];
}
